import chalk from 'chalk';
import ora from 'ora';
import { confirm } from '@inquirer/prompts';
import { requireTeam } from '../lib/auth-guard.js';
import { api } from '../lib/api-client.js';
import { success, error, info, table } from '../lib/output.js';
import { isJsonMode, jsonOutput } from '../lib/globals.js';

/**
 * `agt channels beam setup <agent-code-name>`
 */
export async function channelBeamSetupCommand(
  agentCodeName: string,
  options: {
    directoryUrl?: string;
    skipRegister?: boolean;
    autoPublish?: boolean;
  },
): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;

  const json = isJsonMode();
  const spinner = ora({ text: `Looking up agent "${agentCodeName}"…`, isSilent: json });
  spinner.start();

  try {
    // Look up agent via API
    const agentData = await api.get<{
      agent: {
        agent_id: string;
        code_name: string;
        display_name: string;
        team_id: string;
        channels: string[];
      };
    }>(`/agents/${encodeURIComponent(agentCodeName)}`);

    const agent = agentData.agent;

    // Verify beam is in the agent's channel list
    const channels: string[] = agent.channels ?? [];
    if (!channels.includes('beam')) {
      spinner.fail(`Agent "${agentCodeName}" does not have Beam in its channel list.`);
      error('Enable Beam in the agent\'s channels first (add "beam" to the channels array).');
      process.exitCode = 1;
      return;
    }

    spinner.succeed(`Found agent "${agentCodeName}"`);

    // Check for existing config
    const existingConfig = await api.get<{
      config: Record<string, unknown> | null;
    }>(`/agents/${encodeURIComponent(agentCodeName)}/channel-configs/beam`).catch(() => ({ config: null }));

    if (existingConfig.config?.beam_id) {
      info(`Agent already has Beam identity: ${chalk.cyan(String(existingConfig.config.beam_id))}`);
      const proceed = await confirm({
        message: 'Regenerate identity? This will create a new keypair and re-register.',
        default: false,
      });
      if (!proceed) return;
    }

    // Generate identity and register
    spinner.start('Generating Ed25519 identity and registering with Beam directory…');

    const result = await api.post<{
      ok: boolean;
      beam_id: string;
      did: string;
      public_key: string;
      trust_score: number;
      verification_tier: string;
      published_capabilities: string[];
      error?: string;
    }>(`/agents/${encodeURIComponent(agentCodeName)}/channel-configs/beam/setup`, {
      auto_publish_capabilities: options.autoPublish ?? true,
      directory_url: options.directoryUrl,
    });

    if (!result.ok) {
      spinner.fail('Registration failed');
      error(result.error ?? 'Unknown error');
      process.exitCode = 1;
      return;
    }

    spinner.succeed('Beam identity created and registered');

    if (json) {
      jsonOutput({
        beam_id: result.beam_id,
        did: result.did,
        public_key: result.public_key,
        trust_score: result.trust_score,
        verification_tier: result.verification_tier,
        published_capabilities: result.published_capabilities,
      });
      return;
    }

    console.log();
    table(
      ['Field', 'Value'],
      [
        ['Beam ID', chalk.cyan(result.beam_id)],
        ['DID', chalk.dim(result.did)],
        ['Trust Score', String(result.trust_score)],
        ['Verification', result.verification_tier],
        ['Capabilities', result.published_capabilities.length > 0
          ? result.published_capabilities.join(', ')
          : chalk.dim('none published')],
      ],
    );
    console.log();
    success(`Agent "${agentCodeName}" is now reachable at ${chalk.cyan(result.beam_id)}`);
  } catch (err) {
    spinner.fail('Setup failed');
    error(err instanceof Error ? err.message : 'Unknown error');
    process.exitCode = 1;
  }
}

/**
 * `agt channels beam status <agent-code-name>`
 */
export async function channelBeamStatusCommand(agentCodeName: string): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;

  const json = isJsonMode();
  const spinner = ora({ text: `Fetching Beam config for "${agentCodeName}"…`, isSilent: json });
  spinner.start();

  try {
    const data = await api.get<{
      config: Record<string, unknown> | null;
      status?: string;
    }>(`/agents/${encodeURIComponent(agentCodeName)}/channel-configs/beam`);

    if (!data.config?.beam_id) {
      spinner.info('No Beam identity configured for this agent.');
      return;
    }

    spinner.succeed('Beam configuration found');
    const cfg = data.config;

    if (json) {
      jsonOutput(cfg);
      return;
    }

    console.log();
    table(
      ['Field', 'Value'],
      [
        ['Beam ID', chalk.cyan(String(cfg.beam_id))],
        ['DID', chalk.dim(String(cfg.did ?? '—'))],
        ['Public Key', chalk.dim(String(cfg.public_key ?? '').slice(0, 24) + '…')],
        ['Trust Score', String(cfg.trust_score ?? 0)],
        ['Verification', String(cfg.verification_tier ?? 'basic')],
        ['Auto-publish', cfg.auto_publish_capabilities ? chalk.green('yes') : chalk.dim('no')],
        ['Capabilities', Array.isArray(cfg.published_capabilities) && cfg.published_capabilities.length > 0
          ? cfg.published_capabilities.join(', ')
          : chalk.dim('none')],
        ['Status', data.status ?? '—'],
      ],
    );
    console.log();
  } catch (err) {
    spinner.fail('Failed to fetch config');
    error(err instanceof Error ? err.message : 'Unknown error');
    process.exitCode = 1;
  }
}

/**
 * `agt channels beam remove <agent-code-name>`
 */
export async function channelBeamRemoveCommand(agentCodeName: string): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;

  const json = isJsonMode();

  if (!json) {
    const proceed = await confirm({
      message: `Remove Beam identity from "${agentCodeName}"? The directory entry will expire.`,
      default: false,
    });
    if (!proceed) return;
  }

  const spinner = ora({ text: 'Removing Beam identity…', isSilent: json });
  spinner.start();

  try {
    await api.del(`/agents/${encodeURIComponent(agentCodeName)}/channel-configs/beam`);
    spinner.succeed('Beam identity removed');

    if (json) {
      jsonOutput({ ok: true });
    }
  } catch (err) {
    spinner.fail('Failed to remove');
    error(err instanceof Error ? err.message : 'Unknown error');
    process.exitCode = 1;
  }
}
